import React, { InputHTMLAttributes, useCallback, useMemo } from 'react';

import PropTypes from 'prop-types';
import styledC, { CSSObject } from 'styled-components';

import theme, {
  getInteractiveStyles,
  stylePropType,
  DetailedStylableComponent,
} from '@adsk/alloy-react-theme';

import {
  ICON_SIZE,
  LEFT_PADDING,
  RIGHT_PADDING,
  TEXT_INPUT_TYPES,
} from './consts';
import { useDeprecatedRenderProps, useTextInput } from './hooks';
import { RenderProps } from './hooks/useDeprecatedRenderProps';
import { NonTextInputProps, RenderElementType, TextInputType } from './types';

const getElementStyle = (disabled?: boolean, error?: boolean): CSSObject => ({
  color: theme.tokens.colors.text.secondary.value,
  pointerEvents: 'none',
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',

  ...(disabled && {
    color: theme.tokens.colors.text.inactive.value,
  }),

  ...(error && {
    color: theme.tokens.colors.text.error.value,
  }),
});

type TextInputProps = DetailedStylableComponent<
  Omit<InputHTMLAttributes<HTMLInputElement>, NonTextInputProps | 'value'>,
  HTMLInputElement
> &
  RenderProps & {
    type?: TextInputType;
    disabled?: boolean;
    error?: boolean;
    value?: string;
    softLimit?: number;
    showCountThreshold?: number;
    showCount?: boolean;
    inputClassName?: string;
  };

const StyledDiv = styledC.div<{ disabled: boolean; $error: boolean }>`
  height: 36px;
  position: relative;
  width: 100%;
  background: ${theme.tokens.colors.background.primary.value};
  color: ${theme.tokens.colors.text.primary.value};
  border: 1px solid ${theme.tokens.colors.border.value};
  border-radius: ${theme.borderRadius.small}px;
  box-sizing: border-box;
  padding-left: ${LEFT_PADDING}px;
  padding-right: ${RIGHT_PADDING}px;
  ${(props) => getInteractiveStyles(props.disabled)};
  &:disabled {
    background: ${theme.tokens.colors.background.neutral.inactive.value};
    border-color: ${theme.tokens.colors.border.inactive.value};
    box-shadow: none;
    color: ${theme.tokens.colors.text.inactive.value};
    cursor: not-allowed;
  }

  border-color: ${(props) =>
    props.$error ? theme.tokens.colors.border.error.value : undefined};
  &:focus:not(:active) {
    border-color: ${(props) =>
      props.$error ? undefined : theme.tokens.colors.border.focused.value};
  }
`;

const StyledInput = styledC.input`
  ${theme.typography.labelMedium};
  height: 100%;
  left: 0px;
  outline: none;
  padding: 0px ${theme.spacing.S}px 1px;
  position: absolute;
  width: 100%;
  border: none;
  color: ${theme.tokens.colors.text.primary.value};
  ${(props) => getInteractiveStyles(!!props.disabled)};
  &::placeholder {
    color: ${theme.tokens.colors.text.tertiary.value};
  }
  &:disabled {
    color: ${theme.tokens.colors.text.inactive.value};
    background-color: ${theme.tokens.colors.background.secondary.value};
  }
`;

/**
 * A TextInput component that optionally displays an icon.
 * All arbitrary DOM properties and attributes (including event handlers) are accepted and passed to the HTML input element.
 */

const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      type = TEXT_INPUT_TYPES.TEXT,
      style,
      className,
      disabled = false,
      error = false,
      renderIcon,
      renderLeftIcon,
      renderLeftElement,
      renderRightElement,
      onKeyDown,
      onChange,
      softLimit,
      maxLength,
      showCountThreshold = 15,
      value,
      defaultValue,
      showCount,
      inputClassName,
      ...props
    },
    ref,
  ) => {
    const { renderLeft, renderRight } = useDeprecatedRenderProps({
      renderIcon,
      renderLeftIcon,
      renderLeftElement,
      renderRightElement,
    });

    const {
      refs: { divRef, leftRef, rightRef },
      padding: { paddingLeft, paddingRight },
      propGetters: { getInputProps },
      charCount,
      shouldRenderCount,
    } = useTextInput({
      renderLeft,
      renderRight,
      onKeyDown,
      onChange,
      type,
      ref,
      showCount,
      softLimit,
      maxLength,
      showCountThreshold,
      value,
      defaultValue,
    });

    const renderCount: RenderElementType = useCallback(
      (props) => {
        if (shouldRenderCount) {
          return (
            <div {...props} data-testid="TextInput__count">
              {`${charCount}/${softLimit || maxLength}`}
            </div>
          );
        }

        return null;
      },
      [softLimit, charCount, maxLength, shouldRenderCount],
    );

    const renderRightComponent = useMemo(() => {
      return renderRight ?? renderCount;
    }, [renderRight, renderCount]);

    return (
      <StyledDiv
        ref={divRef}
        className={className}
        disabled={disabled}
        $error={error}
        css={style || {}}
      >
        {renderLeft?.({
          size: ICON_SIZE,
          ref: leftRef,
          style: {
            ...getElementStyle(disabled, error),
            left: paddingLeft,
            zIndex: 1,
          },
        })}
        <StyledInput
          {...props}
          {...getInputProps()}
          disabled={disabled}
          maxLength={maxLength}
          className={inputClassName}
        />
        {renderRightComponent?.({
          size: ICON_SIZE,
          ref: rightRef,
          style: {
            ...getElementStyle(disabled, error),
            right: paddingRight,
          },
        })}
      </StyledDiv>
    );
  },
);

TextInput.displayName = 'TextInput';

TextInput.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Toggle disabled state */
  disabled: PropTypes.bool,
  /** Toggle error state */
  error: PropTypes.bool,
  /** Deprecated: Display icon adjacent to input text on right side */
  renderIcon: PropTypes.func,
  /** Deprecated: Display icon adjacent to input text on left side */
  renderLeftIcon: PropTypes.func,
  /** Placeholder text value */
  placeholder: PropTypes.string,
  /** Type of input value */
  type: PropTypes.oneOf(Object.values(TEXT_INPUT_TYPES)),
  /** Display a React element adjacent to input text on left side */
  renderLeftElement: PropTypes.func,
  /** Display a React element adjacent to input text on right side */
  renderRightElement: PropTypes.func,
  /** Like maxLength but this is a soft threshold and the input does not stop the user from entering characters beyond this amount, but this will show as the maximum number in the count display */
  softLimit: PropTypes.number,
  /** A hard limit on the maximum number of characters permitted in the input field, if softLimit is not provided then this will show as the maximum number in the count display */
  maxLength: PropTypes.number,
  /** If set, this will delay the display of character count until the character count is within this many characters of reaching the maximum length or soft limit length. Default is 15. Set to 0 to always show the count when showCount is enabled. */
  showCountThreshold: PropTypes.number,
  /** If true and maxLength or softLimit is set then display a character count, otherwise do not display the character count */
  showCount: PropTypes.bool,
  /** Class applied to the input element */
  inputClassName: PropTypes.string,
};

export default Object.assign(TextInput, {
  TYPES: TEXT_INPUT_TYPES,
});
